import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createAction, createFeatureSelector, createReducer, createSelector, on, props } from '@ngrx/store';
import { catchError, defaultIfEmpty, of, switchMap } from 'rxjs';
import { saveAs } from 'file-saver-es';
import { EdicaoPublicadaService } from 'src/app/services/edicao-publicada.service';
import { CommonMessages } from 'src/app/utils/common.messages';
import { toastShow } from 'src/app/stateManagement/actions/toast.action';
import { ToastModel, ToastType } from 'src/app/domain/models/toast.model';

const actionName = '[Baixar PDF Matéria Publicada]';

export const baixarPdfMateriaPublicada = createAction(
  `${actionName} - Baixar`,
  props<{ publicacaoId: number }>()
);
export const baixarPdfMateriaPublicadaSuccess = createAction(`${actionName} - Sucesso`);
export const baixarPdfMateriaPublicadaFailure = createAction(
  `${actionName} - Falha`,
  props<{ erro: string }>()
);
export const baixarPdfMateriaPublicadaFinish = createAction(`${actionName} - Concluir`);

export interface BaixarPdfMateriaPublicadaState {
  error: string | null;
  publicacaoIdEmDownload: number | null;
}

const initialState: BaixarPdfMateriaPublicadaState = {
  error: null,
  publicacaoIdEmDownload: null
};

// -- Reducer
export const baixarPdfMateriaPublicadaReducer = createReducer(
  initialState,
  on(baixarPdfMateriaPublicada, (state, { publicacaoId }) => ({
    ...state,
    error: null,
    publicacaoIdEmDownload: publicacaoId
  })),
  on(baixarPdfMateriaPublicadaSuccess, () => initialState),
  on(baixarPdfMateriaPublicadaFailure, (state, { erro }) => ({
    ...state,
    error: erro,
    publicacaoIdEmDownload: null
  })),
  on(baixarPdfMateriaPublicadaFinish, () => initialState)
);

// -- Selector
export const selectBaixarPdfMateriaPublicadaState =
  createFeatureSelector<BaixarPdfMateriaPublicadaState>('baixarPdfMateriaPublicada');
export const selectPublicacaoIdEmDownload = createSelector(
  selectBaixarPdfMateriaPublicadaState,
  state => state.publicacaoIdEmDownload
);
export const selectErrosBaixarPdfMateriaPublicada = createSelector(
  selectBaixarPdfMateriaPublicadaState,
  state => state.error
);

// -- Effect
const falhaComToast = () => [
  baixarPdfMateriaPublicadaFailure({ erro: CommonMessages.EdicaoHtml.BaixarMateriaPublicadaErro }),
  toastShow({
    toastModel: new ToastModel(
      CommonMessages.Alert,
      CommonMessages.EdicaoHtml.BaixarMateriaPublicadaErro,
      ToastType.Warning
    )
  })
];

const extrairNomeArquivo = (contentDisposition: string | null): string | undefined => {
  if (!contentDisposition)
    return undefined;

  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1])
    return decodeURIComponent(utf8[1].trim());

  const ascii = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return ascii?.[1]?.trim();
};

@Injectable()
export class BaixarPdfMateriaPublicadaEffects {
  baixarPdfMateriaPublicada$ = createEffect(() => this.actions$.pipe(
    ofType(baixarPdfMateriaPublicada),
    switchMap(({ publicacaoId }) =>
      this.edicaoPublicadaService.ObterPdfMateriaPublicada(publicacaoId).pipe(
        defaultIfEmpty(null),
        switchMap(response => {
          if (!response?.body || response.body.size === 0)
            return of(...falhaComToast());

          saveAs(response.body, extrairNomeArquivo(response.headers.get('Content-Disposition')));
          return of(baixarPdfMateriaPublicadaSuccess());
        }),
        catchError(() => of(...falhaComToast()))
      )
    )
  ));

  constructor(
    private readonly actions$: Actions,
    private readonly edicaoPublicadaService: EdicaoPublicadaService
  ) {}
}
